import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Trash2, ChevronRight, Pencil } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { formatCents, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableTh } from '@/components/SortableTh';
import { RETURN_STATUS_BADGE } from '@/lib/badges';

// ─── Types ──────────────────────────────────────────────────────────────────

type ReturnStatus = 'open' | 'refunded';

interface ReturnRow {
  id: number;
  number: string;
  estimate_id: number;
  estimate_number: string;
  customer_id: number;
  customer_name: string;
  date: string;
  status: ReturnStatus;
  total: number;
  refunded: number;
  created_at: string;
}

// ─── Module-level constants ──────────────────────────────────────────────────

type StatusFilter = 'all' | ReturnStatus;

const STATUS_TABS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Open', value: 'open' },
  { label: 'Refunded', value: 'refunded' },
];

// ─── Delete Confirm Dialog ────────────────────────────────────────────────────

interface DeleteDialogProps {
  sr: ReturnRow;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}

function DeleteDialog({ sr, onCancel, onConfirm, loading }: DeleteDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Delete Return</h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Delete return <span className="font-semibold text-foreground">{sr.number}</span>? This
            reverses stock and removes refunds. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={onConfirm} disabled={loading}>
              {loading ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ReturnsPage() {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<StatusFilter>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [deletingSR, setDeletingSR] = useState<ReturnRow | undefined>(undefined);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const {
    rows: returns,
    total,
    page,
    setPage,
    limit,
    setLimit,
    sort,
    dir,
    toggleSort,
    q,
    setQ,
    isLoading,
  } = usePaginatedList<ReturnRow>({
    endpoint: '/api/returns',
    entityKey: 'returns',
    defaultSort: 'date',
    defaultDir: 'desc',
    extraParams: {
      status: status !== 'all' ? status : undefined,
      from: from || undefined,
      to: to || undefined,
    },
  });

  async function handleDelete() {
    if (!deletingSR) return;
    setDeleteLoading(true);
    try {
      await apiFetch(`/api/returns/${deletingSR.id}`, { method: 'DELETE' });
      toast.success('Return deleted');
      setDeletingSR(undefined);
      queryClient.invalidateQueries({ queryKey: ['returns'] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete return';
      toast.error(msg);
      setDeletingSR(undefined);
    } finally {
      setDeleteLoading(false);
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Sales Returns</h1>
          {!isLoading && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {total}
            </span>
          )}
        </div>
        <ExportButtons
          endpoint="/api/returns"
          params={{
            status: status !== 'all' ? status : undefined,
            q: q || undefined,
            from: from || undefined,
            to: to || undefined,
          }}
        />
      </div>

      {/* Filters — one line: search · date range · status pills (wraps on small screens) */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search SR # or customer…"
            className="pl-9 h-9"
          />
        </div>

        {/* Date range */}
        <div className="flex items-center gap-2 text-sm shrink-0">
          <span className="text-muted-foreground">From</span>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-40 h-9 text-sm"
          />
          <span className="text-muted-foreground">To</span>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-40 h-9 text-sm"
          />
          {(from || to) && (
            <button
              type="button"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            >
              Clear
            </button>
          )}
        </div>

        {/* Status pills */}
        <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5 shrink-0">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setStatus(tab.value)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                status === tab.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <SortableTh
                label="SR #"
                sortKey="number"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-32"
              />
              <SortableTh
                label="Date"
                sortKey="date"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="hidden sm:table-cell w-28"
              />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Customer</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell w-32">
                Estimate #
              </th>
              <SortableTh
                label="Total"
                sortKey="total"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28"
              />
              <th className="px-4 py-3 text-right font-medium text-muted-foreground w-28 hidden md:table-cell">
                Refunded
              </th>
              <SortableTh
                label="Status"
                sortKey="status"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-24"
              />
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && returns.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                  No sales returns found.
                </td>
              </tr>
            )}
            {returns.map((sr) => {
              const badge = RETURN_STATUS_BADGE[sr.status];
              return (
                <tr key={sr.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/returns/${sr.id}`}
                      className="font-mono text-xs hover:underline underline-offset-4"
                    >
                      {sr.number}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell whitespace-nowrap">
                    {formatDate(sr.date)}
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/customers/${sr.customer_id}`}
                      className="hover:underline underline-offset-4 text-muted-foreground"
                    >
                      {sr.customer_name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <Link
                      to={`/estimates/${sr.estimate_id}`}
                      className="font-mono text-xs hover:underline underline-offset-4 text-muted-foreground"
                    >
                      {sr.estimate_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatCents(sr.total)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-muted-foreground hidden md:table-cell">
                    {formatCents(sr.refunded)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Link to={`/returns/${sr.id}`}>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                          View
                          <ChevronRight className="h-3 w-3 ml-0.5" />
                        </Button>
                      </Link>
                      {isAdmin && sr.status === 'open' && (
                        <Link to={`/returns/${sr.id}/edit`}>
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                        </Link>
                      )}
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:border-destructive/50"
                          onClick={() => setDeletingSR(sr)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <Pagination
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Delete dialog */}
      {deletingSR && (
        <DeleteDialog
          sr={deletingSR}
          onCancel={() => setDeletingSR(undefined)}
          onConfirm={handleDelete}
          loading={deleteLoading}
        />
      )}
    </div>
  );
}
